/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { useNavigate } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as adminActions, TAdminMainMenu } from '@store/slice/admin-slice';
import { actions as userActions } from '@store/slice/user-slice';
import { ADMIN_PATH } from '@constant/admin/route-path';
import IconTextBlock from 'component/common/block/icon-text-block';
import TextBlock from 'component/common/block/text-block';
import Gap from '@component/common/gap';
import SideContainer from '@component/common/architecture/side-container';
import otterBrickLogoImage from '@asset/image/otter-brick-logo.webp';
import ImageTextBlock from 'component/common/block/image-text-block';

const MainMenuContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const ServiceMenuContainer = styled(MainMenuContainer)``;

const SettingMenuContainer = styled(MainMenuContainer)``;

const LOGO_DESIGN = {
  box: {
    gap: size.MARGIN_1,
    width: '100%',
    alignX: 'left',
    margin: `${size.MARGIN_2} 0px ${size.MARGIN_2} 0px`,
    padding: `0px 0px 0px ${size.MARGIN_2}`,
    event: { cursor: 'pointer' },
  },
  image: { height: '70px' },
  text: { size: 'large', color: color.WHITE_1, bold: true },
} as const;

interface IMenuItem {
  menu: TAdminMainMenu;
  label: string;
  icon: 'book' | 'edit' | 'account';
  disabled?: boolean;
}

const SERVICE_MENU_ITEMS: IMenuItem[] = [
  { menu: 'student', label: '학생 관리', icon: 'account' },
  { menu: 'expression', label: '표현 관리', icon: 'book' },
  { menu: 'cloze', label: '빈칸문제 관리', icon: 'edit' },
  { menu: 'vocabulary-book', label: '단어집 관리', icon: 'book' },
];

const createMainMenuDesign = (isClicked: boolean, disabled: boolean) => {
  return {
    text: {
      size: 'medium',
      color: disabled ? color.GRAY_2 : color.WHITE_1,
    },
    icon: {
      size: 'medium',
      kind: 'outlined',
      color: disabled ? color.GRAY_2 : color.WHITE_1,
    },
    box: {
      gap: '10px',
      width: '100%',
      height: '35px',
      alignX: 'left',
      padding: `0 0 0 ${size.MARGIN_2}`,
      backgroundColor: isClicked ? color.BLACK_1 : 'transparent',
      event: disabled
        ? { cursor: 'auto' as const }
        : {
            cursor: 'pointer' as const,
            hover: `
              background-color: ${color.BLACK_1};
              border: none;
            `,
          },
    },
  } as const;
};

const LOGOUT_DESIGN = {
  text: { size: 'medium', color: color.GRAY_2 },
  icon: { size: 'medium', kind: 'outlined', color: color.GRAY_2 },
  box: {
    gap: '10px',
    width: '100%',
    height: '35px',
    alignX: 'left',
    padding: `0 0 0 ${size.MARGIN_2}`,
    event: {
      cursor: 'pointer' as const,
      hover: `
        background-color: ${color.BLACK_1};
        border: none;
      `,
    },
  },
} as const;

const MENU_GAP = size.MARGIN_1;

const SideTab: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const mainMenu = useTypedSelector((state) => state.rootReducer?.adminReducer?.mainMenu) ?? 'student';

  const handleLogoClick = () => {
    navigate(ADMIN_PATH.STUDENT_LIST_PATH);
  };

  const handleMenuClick = (item: IMenuItem) => {
    if (item.disabled) return;
    dispatch(adminActions.storeMainMenu(item.menu));
    if (item.menu === 'student') navigate(ADMIN_PATH.STUDENT_LIST_PATH);
    if (item.menu === 'expression') navigate(ADMIN_PATH.EXPRESSION_LIST_PATH);
    if (item.menu === 'cloze') navigate(ADMIN_PATH.CLOZE_LIST_PATH);
    if (item.menu === 'vocabulary-book') navigate(ADMIN_PATH.VOCABULARY_BOOK_LIST_PATH);
  };

  const handleLogout = () => {
    dispatch(userActions.initialize());
    navigate(ADMIN_PATH.SIGNIN_PATH, { replace: true });
  };

  return (
    <SideContainer
      design={{
        width: size.WEB_SIDE_WIDTH,
        backgroundColor: color.BLACK_2,
        top: '0px',
        align: 'up',
      }}>
      <ImageTextBlock
        src={otterBrickLogoImage}
        alt='otterbrick'
        text='Admin'
        design={LOGO_DESIGN}
        click={{ handler: handleLogoClick }}
      />
      <Gap height={size.MARGIN_2} />
      <ServiceMenuContainer>
        {SERVICE_MENU_ITEMS.map((item) => (
          <React.Fragment key={item.menu}>
            <IconTextBlock
              icon={item.icon}
              text={item.label + (item.disabled ? ' (준비 중)' : '')}
              design={createMainMenuDesign(mainMenu === item.menu, !!item.disabled)}
              click={{ handler: () => handleMenuClick(item) }}
            />
            <Gap height={MENU_GAP} />
          </React.Fragment>
        ))}
      </ServiceMenuContainer>
      <Gap height={MENU_GAP} />
      <SettingMenuContainer>
        <IconTextBlock icon='logout' text='로그아웃' design={LOGOUT_DESIGN} click={{ handler: handleLogout }} />
      </SettingMenuContainer>
    </SideContainer>
  );
};

export default SideTab;
